import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href: string;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  { label: '首頁', href: '/' },
  {
    label: '認識學會',
    href: '/about',
    children: [
      { label: '願景與宗旨', href: '/about#vision' },
      { label: '學會歷程', href: '/about#history' },
      { label: '發展藍圖', href: '/about#roadmap' },
      { label: '組織與團隊', href: '/about#team' },
    ],
  },
  {
    label: '推廣行動',
    href: '/initiatives',
    children: [
      { label: '家庭關係與親職教育', href: '/initiatives#family' },
      { label: '生命教育與生死教育', href: '/initiatives#life' },
      { label: '社區支持與公益行動', href: '/initiatives#community' },
      { label: '教育與企業跨域合作', href: '/initiatives#coop' },
    ],
  },
  { label: '活動與課程', href: '/events' },
  {
    label: '成果與專欄',
    href: '/stories',
    children: [
      { label: '活動成果', href: '/stories#results' },
      { label: '家庭教育專欄', href: '/stories#family-edu' },
      { label: '生命教育專欄', href: '/stories#life-edu' },
      { label: '公益與交流', href: '/stories#charity' },
    ],
  },
  {
    label: '參與支持',
    href: '/support',
    children: [
      { label: '加入會員', href: '/support/member' },
      { label: '志工參與', href: '/support/volunteer' },
      { label: '捐款支持', href: '/support/donate' },
      { label: '合作提案', href: '/cooperate' },
    ],
  },
  { label: '聯絡我們', href: '/contact' },
];

function isActive(href: string, pathname: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname.startsWith(href);
}

function handleAnchorNav(e: React.MouseEvent<HTMLAnchorElement>, href: string, navigate: ReturnType<typeof useNavigate>) {
  if (href.includes('#')) {
    const [path, hash] = href.split('#');
    e.preventDefault();
    if (window.location.pathname === path) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(path);
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const onDropdownEnter = useCallback((label: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpenDropdown(label);
  }, []);

  const onDropdownLeave = useCallback(() => {
    timerRef.current = setTimeout(() => setOpenDropdown(null), 150);
  }, []);

  const onKeyToggle = (e: React.KeyboardEvent, label: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpenDropdown(prev => prev === label ? null : label);
    }
    if (e.key === 'Escape') setOpenDropdown(null);
  };

  return (
    <>
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-background-50/90 backdrop-blur-sm'
        }`}
        aria-label="主導覽"
      >
        <div className="mx-auto px-5 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 py-2" aria-label="台灣家庭生命教育交流推廣學會 首頁">
              <img
                src="https://tflea2015.org/wp-content/uploads/2025/06/%E5%AE%B6%E5%AD%B8%E6%9C%83LOGO_-2.png"
                alt="台灣家庭生命教育交流推廣學會"
                className="h-10 md:h-12 w-auto object-contain"
                width="300"
                height="93"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const active = isActive(item.href, location.pathname);
                const hasChildren = !!item.children?.length;
                const dropdownOpen = openDropdown === item.label;

                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => hasChildren && onDropdownEnter(item.label)}
                    onMouseLeave={onDropdownLeave}
                  >
                    <Link
                      to={item.href}
                      onClick={(e) => {
                        if (hasChildren && dropdownOpen) {
                          e.preventDefault();
                          setOpenDropdown(null);
                        }
                      }}
                      className={`flex items-center gap-1 px-3 py-2 rounded-md text-[15px] font-medium whitespace-nowrap transition-colors ${
                        active
                          ? 'text-primary-700'
                          : 'text-foreground-700 hover:text-primary-600'
                      }`}
                      aria-current={active ? 'page' : undefined}
                      aria-expanded={hasChildren ? dropdownOpen : undefined}
                      aria-haspopup={hasChildren ? 'menu' : undefined}
                      onKeyDown={(e) => hasChildren && onKeyToggle(e, item.label)}
                    >
                      {item.label}
                      {hasChildren && (
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                        </span>
                      )}
                    </Link>

                    {hasChildren && dropdownOpen && (
                      <div
                        className="absolute top-full left-0 mt-1 min-w-[220px] bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 z-50"
                        role="menu"
                      >
                        {item.children?.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={(e) => handleAnchorNav(e, child.href, navigate)}
                            className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50 transition-colors whitespace-nowrap"
                            role="menuitem"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile toggle */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? '關閉選單' : '開啟選單'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu-panel"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                {mobileOpen ? (
                  <i className="ri-close-line text-xl" />
                ) : (
                  <i className="ri-menu-line text-xl" />
                )}
              </span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div
          id="mobile-menu-panel"
          className="fixed inset-0 z-40 lg:hidden"
          role="dialog"
          aria-label="手機選單"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-foreground-950/30"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="px-5 py-6">
              {navItems.map((item) => {
                const active = isActive(item.href, location.pathname);
                const hasChildren = !!item.children?.length;
                const expanded = openDropdown === item.label;

                return (
                  <div key={item.label} className="border-b border-background-200/70 last:border-0">
                    <div className="flex items-center justify-between">
                      <Link
                        to={item.href}
                        onClick={(e) => {
                          if (hasChildren) {
                            e.preventDefault();
                            setOpenDropdown(prev => prev === item.label ? null : item.label);
                          } else {
                            setMobileOpen(false);
                          }
                        }}
                        className={`flex-1 py-3.5 text-base font-medium ${
                          active ? 'text-primary-700' : 'text-foreground-800'
                        }`}
                      >
                        {item.label}
                      </Link>
                      {hasChildren && (
                        <button
                          className="w-10 h-10 flex items-center justify-center text-foreground-500"
                          onClick={() => setOpenDropdown(prev => prev === item.label ? null : item.label)}
                          aria-expanded={expanded}
                          aria-label={`展開 ${item.label} 選單`}
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className={`ri-arrow-down-s-line text-lg transition-transform ${expanded ? 'rotate-180' : ''}`} />
                          </span>
                        </button>
                      )}
                    </div>
                    {hasChildren && expanded && (
                      <div className="pb-3 pl-4 space-y-1">
                        {item.children?.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={(e) => {
                              handleAnchorNav(e, child.href, navigate);
                              if (!child.href.includes('#')) setMobileOpen(false);
                            }}
                            className="block py-2 text-sm text-foreground-600 hover:text-primary-600"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}